"use strict";
// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var algorithm_1 = require("@phosphor/algorithm");
var coreutils_1 = require("@phosphor/coreutils");
var signaling_1 = require("@phosphor/signaling");
var __1 = require("..");
var terminal_1 = require("./terminal");
/**
 * A terminal session manager.
 */
var TerminalManager = /** @class */ (function () {
    /**
     * Construct a new terminal manager.
     */
    function TerminalManager(options) {
        if (options === void 0) { options = {}; }
        var _this = this;
        this._models = [];
        this._sessions = new Set();
        this._isDisposed = false;
        this._isReady = false;
        this._refreshTimer = -1;
        this._runningChanged = new signaling_1.Signal(this);
        this.serverSettings = options.serverSettings || __1.ServerConnection.makeSettings();
        // Set up state handling if terminals are available.
        if (terminal_1.TerminalSession.isAvailable()) {
            // Initialize internal data.
            this._readyPromise = this._refreshRunning();
            // Set up polling.
            this._refreshTimer = setInterval(function () {
                _this._refreshRunning();
            }, 10000);
        }
    }
    Object.defineProperty(TerminalManager.prototype, "runningChanged", {
        /**
         * A signal emitted when the running terminals change.
         */
        get: function () {
            return this._runningChanged;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TerminalManager.prototype, "isDisposed", {
        /**
         * Test whether the terminal manager is disposed.
         */
        get: function () {
            return this._isDisposed;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TerminalManager.prototype, "isReady", {
        /**
         * Test whether the manger is ready.
         */
        get: function () {
            return this._isReady;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Dispose of the resources used by the manager.
     */
    TerminalManager.prototype.dispose = function () {
        if (this.isDisposed) {
            return;
        }
        this._isDisposed = true;
        clearInterval(this._refreshTimer);
        signaling_1.Signal.clearData(this);
        this._models = [];
    };
    Object.defineProperty(TerminalManager.prototype, "ready", {
        /**
         * A promise that fulfills when the manager is ready.
         */
        get: function () {
            return this._readyPromise || Promise.reject('Terminals unavailable');
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Whether the terminal service is available.
     */
    TerminalManager.prototype.isAvailable = function () {
        return terminal_1.TerminalSession.isAvailable();
    };
    /**
     * Create an iterator over the most recent running terminals.
     *
     * @returns A new iterator over the running terminals.
     */
    TerminalManager.prototype.running = function () {
        return algorithm_1.iter(this._models);
    };
    /**
     * Create a new terminal session.
     *
     * @param options - The options used to connect to the session.
     *
     * @returns A promise that resolves with the terminal instance.
     *
     * #### Notes
     * The manager `serverSettings` will be used unless overridden in the
     * options.
     */
    TerminalManager.prototype.startNew = function (options) {
        var _this = this;
        return terminal_1.TerminalSession.startNew(this._getOptions(options)).then(function (session) {
            _this._onStarted(session);
            return session;
        });
    };
    /*
     * Connect to a running session.
     *
     * @param name - The name of the target session.
     *
     * @param options - The options used to connect to the session.
     *
     * @returns A promise that resolves with the new session instance.
     *
     * #### Notes
     * The manager `serverSettings` will be used unless overridden in the
     * options.
     */
    TerminalManager.prototype.connectTo = function (name, options) {
        var _this = this;
        return terminal_1.TerminalSession.connectTo(name, this._getOptions(options)).then(function (session) {
            _this._onStarted(session);
            return session;
        });
    };
    /**
     * Shut down a terminal session by name.
     */
    TerminalManager.prototype.shutdown = function (name) {
        var _this = this;
        var index = algorithm_1.ArrayExt.findFirstIndex(this._models, function (value) { return value.name === name; });
        if (index === -1) {
            return;
        }
        // Proactively remove the model.
        this._models.splice(index, 1);
        this._runningChanged.emit(this._models.slice());
        return terminal_1.TerminalSession.shutdown(name, this.serverSettings).then(function () {
            var toRemove = [];
            _this._sessions.forEach(function (s) {
                if (s.name === name) {
                    s.dispose();
                    toRemove.push(s);
                }
            });
            toRemove.forEach(function (s) { _this._sessions.delete(s); });
        });
    };
    /**
     * Shut down all terminal sessions.
     *
     * @returns A promise that resolves when all of the sessions are shut down.
     */
    TerminalManager.prototype.shutdownAll = function () {
        var _this = this;
        // Proactively remove all models.
        var models = this._models;
        if (models.length > 0) {
            this._models = [];
            this._runningChanged.emit([]);
        }
        return this._refreshRunning().then(function () {
            return Promise.all(models.map(function (model) {
                return terminal_1.TerminalSession.shutdown(model.name, _this.serverSettings).then(function () {
                    var toRemove = [];
                    _this._sessions.forEach(function (s) {
                        s.dispose();
                        toRemove.push(s);
                    });
                    toRemove.forEach(function (s) { _this._sessions.delete(s); });
                });
            })).then(function () { return undefined; });
        });
    };
    /**
     * Force a refresh of the running sessions.
     *
     * @returns A promise that with the list of running sessions.
     *
     * #### Notes
     * This is not typically meant to be called by the user, since the
     * manager maintains its own internal state.
     */
    TerminalManager.prototype.refreshRunning = function () {
        return this._refreshRunning();
    };
    /**
     * Handle a session terminating.
     */
    TerminalManager.prototype._onTerminated = function (name) {
        var index = algorithm_1.ArrayExt.findFirstIndex(this._models, function (value) { return value.name === name; });
        if (index !== -1) {
            this._models.splice(index, 1);
            this._runningChanged.emit(this._models.slice());
        }
    };
    /**
     * Handle a session starting.
     */
    TerminalManager.prototype._onStarted = function (session) {
        var _this = this;
        var name = session.name;
        this._sessions.add(session);
        var index = algorithm_1.ArrayExt.findFirstIndex(this._models, function (value) { return value.name === name; });
        if (index === -1) {
            this._models.push(session.model);
            this._runningChanged.emit(this._models.slice());
        }
        session.terminated.connect(function () {
            _this._onTerminated(name);
        });
    };
    /**
     * Refresh the running sessions.
     */
    TerminalManager.prototype._refreshRunning = function () {
        var _this = this;
        return terminal_1.TerminalSession.listRunning(this.serverSettings).then(function (models) {
            _this._isReady = true;
            if (!coreutils_1.JSONExt.deepEqual(models, _this._models)) {
                var names_1 = models.map(function (r) { return r.name; });
                var toRemove_1 = [];
                _this._sessions.forEach(function (s) {
                    if (names_1.indexOf(s.name) === -1) {
                        s.dispose();
                        toRemove_1.push(s);
                    }
                });
                toRemove_1.forEach(function (s) { _this._sessions.delete(s); });
                _this._models = models.slice();
                _this._runningChanged.emit(models);
            }
        });
    };
    /**
     * Get a set of options to pass.
     */
    TerminalManager.prototype._getOptions = function (options) {
        if (options === void 0) { options = {}; }
        return __assign({}, options, { serverSettings: this.serverSettings });
    };
    return TerminalManager;
}());
exports.TerminalManager = TerminalManager;
